<script lang="ts">
	/**
	 * Google Analytics 4.
	 *
	 * Rendered once from `+layout.svelte`, so the tag lands in every prerendered
	 * page's `<head>`.
	 *
	 * **Page views are sent manually, not automatically.** This is a SvelteKit app
	 * with a client-side router: the layout mounts once and every subsequent
	 * in-site navigation is a `pushState`, not a document load. `gtag.js` sends its
	 * automatic `page_view` only on that first load, so an earlier version of this
	 * component — which configured GA in the head *and* again in `onMount` —
	 * double-counted the entry page and then recorded nothing for the rest of the
	 * visit. Both bugs come from the same cause, so both are fixed the same way:
	 *
	 * - `send_page_view: false` suppresses the automatic hit entirely.
	 * - `afterNavigate` fires on the initial navigation *and* on every client-side
	 *   one, so each view is sent exactly once from exactly one place.
	 *
	 * `afterNavigate` is used rather than a `$effect` on `page.url` because it runs
	 * after the DOM has settled, so `document.title` is the new page's title rather
	 * than the previous one's.
	 */
	import { dev } from '$app/environment';
	import { afterNavigate } from '$app/navigation';
	import { GA_TAG_ID } from '$lib/seo/config';

	/**
	 * No analytics from `vite dev`.
	 *
	 * The measurement ID has a committed default (see `seo/config.ts`), so without
	 * this every `npm run dev` session — on any clone, from anyone — would report
	 * localhost page views into the live property and quietly corrupt the numbers.
	 * `dev` is false during the prerender that produces the deployed pages and
	 * true only under the dev server, which is exactly the distinction wanted.
	 */
	const enabled = !dev && Boolean(GA_TAG_ID);

	afterNavigate((navigation) => {
		if (!enabled || typeof gtag === 'undefined') return;

		gtag('event', 'page_view', {
			page_title: document.title,
			page_location: window.location.href,
			page_path: navigation.to?.url.pathname ?? window.location.pathname
		});
	});
</script>

<svelte:head>
	<!--
		No measurement ID means no tag at all, rather than a script pointing at an
		empty property — and likewise under the dev server, which must never report
		into the live property. See `enabled` above.

		The `{#if}` is inside `<svelte:head>` rather than around it: Svelte requires
		`<svelte:head>` to be at the top level of a component, not nested in a block.
	-->
	{#if enabled}
		<!--
			**Both the src and the config use GA_TAG_ID, not the measurement ID.**
			Verified in a real browser: `gtag('config', …)` with an ID other than the
			one in the `src` makes gtag.js fetch that ID as a separate destination —
			`gtag/js?id=G-TF56R9EQ9M&cx=c&gtm=…`, which answers 404 for this property.
			The destination then never initialises and `/g/collect` is never called, so
			the tag loads, logs nothing to the console, and records nothing.

			Configuring the Google tag ID instead makes the container resolve its own
			destinations internally. The hit still reaches the right property: on the
			wire `collect` carries `tid=G-TF56R9EQ9M`. It also reaches the AW-17132300684
			Ads destination the same tag carries, which is the tag's configured
			behaviour.

			See `seo/config.ts` for how the two IDs differ and how to find the tag ID.
		-->
		<script async src={`https://www.googletagmanager.com/gtag/js?id=${GA_TAG_ID}`}></script>
		<!-- eslint-disable-next-line svelte/no-at-html-tags -->
		{@html `<script>
			window.dataLayer = window.dataLayer || [];
			function gtag() { dataLayer.push(arguments); }
			gtag('js', new Date());
			// send_page_view: the component sends views itself — see the note above.
			gtag('config', '${GA_TAG_ID}', { send_page_view: false });
		<\/script>`}
	{/if}
</svelte:head>
